import { useEffect, useState } from 'react';
import styles from './WithdrawReview.module.css';
import { authService } from '../../services/auth.service';
import {
  withdrawReviewService,
  WithdrawReviewRequest,
  WithdrawReviewFilters,
  WithdrawReviewPageInfo,
} from '../../services/withdrawReview.service';
import {
  getAllEnvironmentsService,
  Environment,
} from '../../services/getAllEnvironment.service';

const STATUS_OPTIONS = ['PENDING', 'APPROVED', 'DECLINED', 'USED'];
const PAGE_SIZE = 20;

const formatDate = (dateStr?: string | null) =>
  dateStr
    ? new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const formatAmount = (amount?: number | null) =>
  amount == null ? '—' : `$${Number(amount).toFixed(2)}`;

const formatFactor = (factor?: number | null) =>
  factor == null ? '—' : Number(factor).toFixed(2);

const statusClass = (status: string): string => {
  switch (status?.toUpperCase()) {
    case 'PENDING': return styles.statusPending;
    case 'APPROVED': return styles.statusApproved;
    case 'DECLINED': return styles.statusDeclined;
    case 'USED': return styles.statusUsed;
    default: return styles.statusDefault;
  }
};

const WithdrawReview = () => {
  const currentUser = authService.getStoredUser();
  const isAdmin = currentUser?.role?.name === 'ADMIN';
  const userEnvironmentId = (currentUser as any)?.environment?.id as number | undefined;

  const [requests, setRequests] = useState<WithdrawReviewRequest[]>([]);
  const [pageInfo, setPageInfo] = useState<WithdrawReviewPageInfo | null>(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Admin environment filter
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [selectedEnvId, setSelectedEnvId] = useState<number | ''>('');

  // Filters
  const [filterStatus, setFilterStatus] = useState<string>('PENDING');
  const [filterUsername, setFilterUsername] = useState<string>('');

  // Detail panel
  const [selectedRequest, setSelectedRequest] = useState<WithdrawReviewRequest | null>(null);

  // Action form
  const [approvedAmount, setApprovedAmount] = useState<string>('');
  const [declineReason, setDeclineReason] = useState<string>('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      getAllEnvironmentsService.getAllEnvironments().then(setEnvironments).catch(console.error);
    }
  }, [isAdmin]);

  const fetchRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: WithdrawReviewFilters = {
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      };
      const envId = isAdmin
        ? (selectedEnvId ? Number(selectedEnvId) : undefined)
        : userEnvironmentId;
      if (envId) filters.environmentId = envId;
      if (filterStatus) filters.status = filterStatus;
      if (filterUsername.trim()) filters.username = filterUsername.trim();
      const data = await withdrawReviewService.getRequests(filters);
      setRequests(data.requests);
      setPageInfo(data.pageInfo);
    } catch (err: any) {
      setError(err?.message || 'Failed to load withdraw review requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEnvId, filterStatus, page]);

  // Reset to the first page (which refetches via effect), or refetch directly
  // when already on the first page.
  const triggerSearch = () => {
    if (page === 0) {
      fetchRequests();
    } else {
      setPage(0);
    }
  };

  const handleUsernameSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      triggerSearch();
    }
  };

  const handleClear = () => {
    setFilterStatus('');
    setFilterUsername('');
    setSelectedEnvId('');
    setPage(0);
  };

  const openRequest = (request: WithdrawReviewRequest) => {
    setSelectedRequest(request);
    setApprovedAmount('');
    setDeclineReason('');
  };

  const closePanel = () => {
    setSelectedRequest(null);
    setApprovedAmount('');
    setDeclineReason('');
  };

  const applyUpdated = (updated: WithdrawReviewRequest) => {
    setRequests((prev) =>
      prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)),
    );
    setSelectedRequest((prev) => (prev ? { ...prev, ...updated } : prev));
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;
    setActionLoading(true);
    try {
      const amount = approvedAmount.trim();
      const updated = await withdrawReviewService.approve({
        id: selectedRequest.id,
        approvedAmount: amount ? Number(amount) : undefined,
      });
      applyUpdated(updated);
      setApprovedAmount('');
      await fetchRequests();
    } catch (err: any) {
      alert(err?.message || 'Failed to approve request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecline = async () => {
    if (!selectedRequest) return;
    setActionLoading(true);
    try {
      const reason = declineReason.trim();
      const updated = await withdrawReviewService.decline({
        id: selectedRequest.id,
        declineReason: reason || undefined,
      });
      applyUpdated(updated);
      setDeclineReason('');
      await fetchRequests();
    } catch (err: any) {
      alert(err?.message || 'Failed to decline request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReopen = async () => {
    if (!selectedRequest) return;
    setActionLoading(true);
    try {
      const updated = await withdrawReviewService.reopen({ id: selectedRequest.id });
      applyUpdated(updated);
      await fetchRequests();
    } catch (err: any) {
      alert(err?.message || 'Failed to reopen request');
    } finally {
      setActionLoading(false);
    }
  };

  const isOpen = Boolean(selectedRequest);
  const status = selectedRequest?.status?.toUpperCase();

  return (
    <main className={styles.main}>
      {/* Overlay */}
      <div
        className={`${styles.overlay} ${isOpen ? styles.open : ''}`}
        onClick={closePanel}
      />

      {/* Header */}
      <div className={styles.headerRow}>
        <h1 className={styles.title}>Withdraw Review Requests</h1>
        {isAdmin && environments.length > 0 && (
          <select
            className={styles.formSelect}
            value={selectedEnvId}
            onChange={(e) => {
              setSelectedEnvId(e.target.value ? Number(e.target.value) : '');
              setPage(0);
            }}
          >
            <option value="">All Environments</option>
            {environments.map((env) => (
              <option key={env.id} value={env.id}>
                {env.name} — {env.host}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Filters */}
      <div className={styles.filtersRow}>
        <select
          className={styles.filterSelect}
          value={filterStatus}
          onChange={(e) => {
            setFilterStatus(e.target.value);
            setPage(0);
          }}
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <input
          className={styles.filterInput}
          type="text"
          placeholder="Username..."
          value={filterUsername}
          onChange={(e) => setFilterUsername(e.target.value)}
          onKeyDown={handleUsernameSearch}
        />
        <button className={styles.filterBtn} onClick={triggerSearch}>Search</button>
        <button className={styles.filterClearBtn} onClick={handleClear}>Clear</button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.loading}>Loading requests...</div>
      ) : requests.length === 0 ? (
        <div className={styles.noData}>No withdraw review requests found</div>
      ) : (
        <>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>User</th>
                  <th>Status</th>
                  <th>Channel</th>
                  <th>Requested</th>
                  <th>Approved</th>
                  <th>Factor</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((request) => (
                  <tr
                    key={request.id}
                    onClick={() => openRequest(request)}
                    className={selectedRequest?.id === request.id ? styles.selectedRow : ''}
                  >
                    <td>{request.id}</td>
                    <td>{request.user?.username}</td>
                    <td>
                      <span className={`${styles.badge} ${statusClass(request.status)}`}>
                        {request.status}
                      </span>
                    </td>
                    <td>{request.channel}</td>
                    <td className={styles.amount}>{formatAmount(request.requestedAmount)}</td>
                    <td className={styles.amount}>{formatAmount(request.approvedAmount)}</td>
                    <td>{formatFactor(request.factorAtRequest)}</td>
                    <td>{formatDate(request.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pageInfo && (
            <div className={styles.pagination}>
              <div className={styles.paginationInfo}>
                Showing {pageInfo.total === 0 ? 0 : pageInfo.offset + 1}–
                {pageInfo.offset + requests.length} of {pageInfo.total}
              </div>
              <div className={styles.paginationControls}>
                <button
                  className={styles.pageBtn}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={!pageInfo.hasPreviousPage}
                >
                  ‹ Prev
                </button>
                <span className={styles.pageIndicator}>Page {page + 1}</span>
                <button
                  className={styles.pageBtn}
                  onClick={() => setPage((p) => p + 1)}
                  disabled={!pageInfo.hasNextPage}
                >
                  Next ›
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Detail Panel */}
      <div className={`${styles.detailPanel} ${isOpen ? styles.open : ''}`}>
        {selectedRequest && (
          <>
            <div className={styles.detailPanelHeader}>
              <div className={styles.detailTitle}>
                #{selectedRequest.id} — {selectedRequest.user?.username}
              </div>
              <button className={styles.closeBtn} onClick={closePanel}>×</button>
            </div>

            <div className={styles.detailBody}>
              {/* Info */}
              <div className={styles.detailSection}>
                <div className={styles.detailSectionTitle}>Request Details</div>
                <div className={styles.infoGrid}>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Status</span>
                    <span className={styles.infoValue}>
                      <span className={`${styles.badge} ${statusClass(selectedRequest.status)}`}>
                        {selectedRequest.status}
                      </span>
                    </span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Channel</span>
                    <span className={styles.infoValue}>{selectedRequest.channel}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Requested Amount</span>
                    <span className={styles.infoValue}>{formatAmount(selectedRequest.requestedAmount)}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Approved Amount</span>
                    <span className={styles.infoValue}>{formatAmount(selectedRequest.approvedAmount)}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Latest Cash-In</span>
                    <span className={styles.infoValue}>{formatAmount(selectedRequest.latestCashInAmount)}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Balance At Request</span>
                    <span className={styles.infoValue}>{formatAmount(selectedRequest.currentBalanceAtRequest)}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Factor At Request</span>
                    <span className={styles.infoValue}>{formatFactor(selectedRequest.factorAtRequest)}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Environment</span>
                    <span className={styles.infoValue}>{selectedRequest.environment?.name}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Factor Threshold</span>
                    <span className={styles.infoValue}>{formatFactor(selectedRequest.environment?.withdrawReviewFactorThreshold)}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Hard Threshold</span>
                    <span className={styles.infoValue}>{formatAmount(selectedRequest.environment?.withdrawReviewHardThreshold)}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Reviewed By</span>
                    <span className={styles.infoValue}>{selectedRequest.reviewedBy?.username ?? '—'}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Reviewed At</span>
                    <span className={styles.infoValue}>{formatDate(selectedRequest.reviewedAt)}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Created</span>
                    <span className={styles.infoValue}>{formatDate(selectedRequest.createdAt)}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Used At</span>
                    <span className={styles.infoValue}>{formatDate(selectedRequest.usedAt)}</span>
                  </div>
                </div>
                {selectedRequest.declineReason && (
                  <div className={styles.declineBox}>
                    <strong>Decline reason:</strong> {selectedRequest.declineReason}
                  </div>
                )}
              </div>

              {/* Actions */}
              {status === 'PENDING' && (
                <div className={styles.detailSection}>
                  <div className={styles.detailSectionTitle}>Approve</div>
                  <div className={styles.actionGroup}>
                    <label className={styles.actionLabel}>Approved Amount</label>
                    <input
                      className={styles.actionInput}
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Leave blank to approve full amount"
                      value={approvedAmount}
                      onChange={(e) => setApprovedAmount(e.target.value)}
                    />
                    <span className={styles.actionHint}>
                      Blank approves the full requested amount ({formatAmount(selectedRequest.requestedAmount)}).
                    </span>
                  </div>
                  <button
                    className={styles.approveBtn}
                    onClick={handleApprove}
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Working...' : 'Approve'}
                  </button>

                  <div className={styles.detailSectionTitle} style={{ marginTop: 12 }}>Decline</div>
                  <div className={styles.actionGroup}>
                    <label className={styles.actionLabel}>Decline Reason (optional)</label>
                    <input
                      className={styles.actionInput}
                      type="text"
                      placeholder="Reason..."
                      value={declineReason}
                      onChange={(e) => setDeclineReason(e.target.value)}
                    />
                  </div>
                  <button
                    className={styles.declineBtn}
                    onClick={handleDecline}
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Working...' : 'Decline'}
                  </button>
                </div>
              )}

              {(status === 'APPROVED' || status === 'DECLINED') && (
                <div className={styles.detailSection}>
                  <div className={styles.detailSectionTitle}>Actions</div>
                  <button
                    className={styles.reopenBtn}
                    onClick={handleReopen}
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Working...' : 'Reopen'}
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </main>
  );
};

export default WithdrawReview;
